import { useEffect, useState } from "react";
import { X, Check, Settings as Cog, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ManageSituationOptions from "./ManageSituationOptions";

/**
 * Diálogo "Situación de la reparación" (iter 69).
 *
 * Flujo:
 *  1. El usuario elige una de las 3 grandes (Acepta / Rechaza / Sin reparación).
 *  2. Se muestra un desplegable con las opciones ACTIVAS de ese grupo.
 *  3. (Opcional) "Gestionar opciones" abre el modal anidado.
 *  4. "Guardar situación" hace PUT /api/repairs/{id}/situation.
 *
 * Props:
 *  - repairId
 *  - currentSituation: {main, detail, detail_label} actual de la reparación.
 *  - isAdmin: bool — habilita Gestionar opciones.
 *  - onClose, onSaved(repair)
 */
const MAIN_OPTIONS = [
  { id: "accept", label: "Acepta reparación", color: "emerald", icon: CheckCircle2 },
  { id: "reject", label: "Rechaza reparación", color: "red", icon: XCircle },
  { id: "no_repair", label: "Sin reparación", color: "amber", icon: MinusCircle },
];

export default function SituationDialog({ repairId, currentSituation, isAdmin, onClose, onSaved }) {
  const [main, setMain] = useState(currentSituation?.main || "");
  const [detailId, setDetailId] = useState(currentSituation?.detail || "");
  const [options, setOptions] = useState({ accept: [], reject: [], no_repair: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const loadOptions = () => {
    setLoading(true);
    api.get("/settings/situation-options")
      .then((r) => setOptions(r.data || { accept: [], reject: [], no_repair: [] }))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(loadOptions, []);

  // Solo opciones activas en el desplegable. Si el detalle actual está
  // desactivado, igualmente lo incluimos para que el usuario lo vea
  // (con sufijo "(inactiva)") pero no podrá guardarlo de nuevo.
  const activeDetail = (main && options[main])
    ? options[main].filter((o) => o.active || o.id === currentSituation?.detail)
    : [];

  const pickMain = (id) => {
    setMain(id);
    // Si el nuevo grupo no incluye el detalle actual, seleccionamos el primero activo.
    const list = options[id] || [];
    const stillValid = list.some((o) => o.id === detailId && o.active);
    if (!stillValid) {
      const firstActive = list.find((o) => o.active);
      setDetailId(firstActive ? firstActive.id : "");
    }
  };

  const save = async () => {
    if (!main || !detailId) {
      toast.error("Selecciona situación y un detalle");
      return;
    }
    setSaving(true);
    try {
      const chosen = (options[main] || []).find((o) => o.id === detailId);
      const r = await api.put(`/repairs/${repairId}/situation`, {
        main, detail_id: detailId,
        detail_label: chosen?.label || "",
      });
      toast.success("Situación guardada");
      onSaved?.(r.data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="situation-dialog"
      >
        <div
          className="bg-white border border-[#111110] shadow-hard w-full max-w-xl max-h-[90vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E0] sticky top-0 bg-white">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                // Estado de aceptación
              </div>
              <div className="font-display font-bold text-base">Situación de la reparación</div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]" data-testid="situation-close">
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* 3 opciones grandes */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {MAIN_OPTIONS.map((m) => {
                const Icon = m.icon;
                const selected = main === m.id;
                const colors = {
                  emerald: selected ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-[#E5E5E0] hover:border-emerald-400",
                  red: selected ? "border-red-600 bg-red-50 text-red-800" : "border-[#E5E5E0] hover:border-red-400",
                  amber: selected ? "border-amber-600 bg-amber-50 text-amber-800" : "border-[#E5E5E0] hover:border-amber-400",
                }[m.color];
                return (
                  <button
                    key={m.id}
                    onClick={() => pickMain(m.id)}
                    data-testid={`situation-main-${m.id}`}
                    className={`border-2 ${colors} px-3 py-3 text-sm font-semibold flex flex-col items-center gap-1 transition-colors`}
                  >
                    <Icon size={20} />
                    {m.label}
                  </button>
                );
              })}
            </div>

            {/* Desplegable de detalle */}
            {main && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
                  Detalle
                </label>
                <select
                  value={detailId}
                  onChange={(e) => setDetailId(e.target.value)}
                  disabled={loading}
                  data-testid="situation-detail"
                  className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
                >
                  <option value="">— Selecciona —</option>
                  {activeDetail.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}{!o.active ? " (inactiva)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Gestionar */}
            <div className="border-t border-[#E5E5E0] pt-3 flex justify-between items-center">
              <button
                onClick={() => setShowManage(true)}
                disabled={!isAdmin}
                title={isAdmin ? "" : "Solo administradores"}
                data-testid="situation-manage-open"
                className="inline-flex items-center gap-1 border border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-xs disabled:opacity-50"
              >
                <Cog size={12} /> Gestionar opciones
              </button>
              <button
                onClick={save}
                disabled={saving || loading || !main || !detailId}
                data-testid="situation-save"
                className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
              >
                <Check size={14} /> {saving ? "Guardando..." : "Guardar situación"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showManage && (
        <ManageSituationOptions
          options={options}
          isAdmin={isAdmin}
          onClose={() => setShowManage(false)}
          onSaved={(newOpts) => setOptions(newOpts)}
        />
      )}
    </>
  );
}

/**
 * Badge inline para mostrar la situación actual junto a la firma.
 * Reutilizable desde RepairForm.
 */
export function SituationBadge({ situation }) {
  if (!situation?.main) return null;
  const styles = {
    accept: "bg-emerald-100 text-emerald-900 border-emerald-300",
    reject: "bg-red-100 text-red-900 border-red-300",
    no_repair: "bg-amber-100 text-amber-900 border-amber-300",
  };
  const labels = {
    accept: "Acepta reparación",
    reject: "Rechaza reparación",
    no_repair: "Sin reparación",
  };
  return (
    <span
      data-testid="situation-badge"
      data-situation={situation.main}
      className={`inline-flex items-center gap-1 border ${styles[situation.main]} px-2 py-0.5 text-[11px] font-semibold rounded-full`}
    >
      {labels[situation.main]}
      {situation.detail_label ? ` — ${situation.detail_label}` : ""}
    </span>
  );
}
